import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum SmashFoodsPOv4LineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // SKU / ID of the item
  skuDescription = 'skuDescription', // descriptive name of the item (renamed from 'name')
  details = 'details', // verbose description of the item, optional (renamed from 'description')
  vendorSku = 'vendorSku', // vendor's SKU identifier, optional
  vendorSkuDescription = 'vendorSkuDescription', // vendor's SKU description, optional
  uom = 'uom', // unit of measure
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  freightTerms = 'freightTerms', // shipping terms, optional
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const SmashFoodsPOv4addressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
  combineAddressLines: yup.string().nullable().optional(),
  breakCityStateZip: yup.string().nullable().optional(),
})
export type SmashFoodsPOv4FileAddressData = yup.InferType<typeof SmashFoodsPOv4addressInfoSchema>

const miscChargeSchema = yup.object().shape({
  label: yup.string().nullable().optional(), // e.g. "Tax", "Shipping", "Freight"
  value: yup.string().nullable().optional(), // dollar amount
  percentage: yup.string().nullable().optional(), // e.g. "5" → renders "Tax (5%)"
})

const lineItemSchema = yup.object().shape({
  itemNumber: yup.number().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

const SmashFoodsPOv4itemHeadersSchema = yup.object().shape({
  itemNumber: yup.string().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export const SmashFoodsPOv4FileDataSchema = yup.object().shape({
  issuer: SmashFoodsPOv4addressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  acceptableDeliveryData: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  destination: SmashFoodsPOv4addressInfoSchema,
  vendor: SmashFoodsPOv4addressInfoSchema,
  itemHeaders: SmashFoodsPOv4itemHeadersSchema,
  items: yup.array().of(lineItemSchema).required(),
  items2: yup.array().of(lineItemSchema).optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  notes: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
  termsAndConditionsUrl: yup.string().nullable().optional(),
  termsAndConditionsLabel: yup.string().nullable().optional(),
  miscCharges: yup.array().of(miscChargeSchema).optional(),
  currency: yup.string().nullable().optional(),
})

export type SmashFoodsPOv4FileData = yup.InferType<typeof SmashFoodsPOv4FileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<SmashFoodsPOv4FileData>

export const smashFoodsPOv4FileMappings: RequiredFileData = {
  issuer: {
    attn: 'bed57227-842a-4629-9177-a10e9a090127',
    companyName: 'f835927c-f514-4383-8525-fe1d51b8f1d4',
    addressLine1: '8047c84f-36a5-44d6-85e9-78aba571701b',
    addressLine2: '837ce958-bc6f-4a6a-afa0-d7c57c28caad',
    city: '672643b2-0736-48c8-af90-c9725210b4c1',
    state: '2edc885f-2310-451f-9bc4-8a7116b00281',
    zipCode: '9021a0c1-5916-4d57-ae53-ad035fc1e1cf',
    country: '3ba41aff-2e2d-4172-997d-6bf1234f13b1',
    phoneNumber: '258576bb-442e-4cd3-8986-7dfd7c3b7359',
    emailAddress: 'ea629507-e07b-4f01-842a-b6eaf3a32d18',
    displayMultipleLines: 'fecacbce-ee91-46bc-b63d-743a682ca342',
    combineAddressLines: 'd41ef8e7-4ac0-4216-b47d-a763b63a7b2b',
    breakCityStateZip: 'b1e6f2a0-3c47-4d8e-9a5b-7f0c2d6e4a91',
  },
  orderNumber: 'fc92d7f7-d273-4391-8502-6c2c737ec50d',
  paymentTerms: '71f834a3-7ed2-43bc-b697-4b0d4ae97ffb',
  dateIssued: 'bab95cad-0b11-4683-8713-64d771ca3d3f',
  acceptableDeliveryData: 'f4c1d834-4386-4eb8-97ea-832b98c7917a',
  dateReady: 'ba98b63f-7b50-4a95-b07b-b226e55177d0',
  destination: {
    attn: '977d9161-ce2c-4e19-96ae-333da9c73041',
    companyName: '86692e08-b925-462f-9557-18269d5d4730',
    addressLine1: '2e1a0533-0105-4bcb-8ad3-f2387ca5d43a',
    addressLine2: 'deff995f-0975-4842-9402-d40563cd1be7',
    city: 'ab9c387e-7106-4333-9e1c-d0fef704cb23',
    state: '1ab86ea2-1637-42ab-8c8d-81f8222b44dc',
    zipCode: 'eccb0d6d-7adb-41f5-959b-e10d901d38ae',
    country: '7388ef1d-3b93-4e70-ae1c-64b0b0e8a8fc',
    phoneNumber: 'd567f3eb-7d1c-4d03-b873-4d1b5c37085f',
    emailAddress: '3b6a72e6-8d53-4f1d-aa01-f43df3e4eff4',
    displayMultipleLines: '89085b9c-586a-431e-a502-1ab48a66d1f5',
    combineAddressLines: '7024a9a4-c018-4bb7-85ea-2a896d8a88e9',
    breakCityStateZip: 'c2f7a3b1-4d58-4e9f-8b6c-9a1d3e7f5b02',
  },
  vendor: {
    attn: 'c875aa14-9777-491f-b1d7-0e74a296e6bc',
    companyName: '9a0749ea-5f71-4ef3-9705-374d63ddd5e1',
    addressLine1: 'e94385ac-7b13-4872-b60f-7c52d9930a59',
    addressLine2: '47d07fab-7279-41fc-a582-7c2d0df17227',
    city: '590eae45-54ed-4eb2-9b9d-d1e7bc6ee3d0',
    state: '342e3cf0-0523-4a89-83c8-16f45c7d9b33',
    zipCode: '1d049469-f7b2-436c-93cd-8c660961ee6b',
    country: 'ed6a0503-1f54-428b-b663-c0ac332ed49e',
    phoneNumber: '0b0cd085-ecc9-4c92-89c4-150d26cfc79c',
    emailAddress: '318ddf4b-2810-4b30-be77-95219121b51c',
    displayMultipleLines: '6a68ab49-9eab-46b1-a271-786463f8f766',
    combineAddressLines: 'cceffe9d-ef89-4174-8d8c-0fb96173cce1',
    breakCityStateZip: 'd3a8b4c2-5e69-4f0a-9c7d-0b2e4f8a6c13',
  },
  itemHeaders: {
    itemNumber: '222d49d6-5571-4d04-b8c6-5beb1e39a058',
    itemName: '90726c37-bcc6-429b-9921-deea66686097',
    skuDescription: '8626c948-bf00-4117-a084-41d30ed523e8',
    details: '7f99b7cf-5f30-4f50-b9e6-13f6d2effed3',
    vendorSku: 'e4fb2347-dcd7-43c0-a29c-e4cafabc84e2',
    vendorSkuDescription: '3cef6aa5-96ac-4705-a473-6c1df8ffbd6a',
    uom: '7c5e4de6-a533-4a18-9e0c-640713482d93',
    itemQty: '85401c5e-4a33-43e2-b9c8-1e8994883669',
    unitPrice: '20ccbec4-ff1e-4de8-b201-8cae0fa60cbb',
    freightTerms: '5611a96c-b2f5-47dd-99f0-c2a495d40615',
    lineTotal: '2722ce40-6595-41df-bc95-652a4a6a6138',
  },
  items: [
    {
      itemNumber: null,
      itemName: '898a0b7b-6d41-4428-afba-9c42b448a5d3',
      skuDescription: 'e43629cc-b745-44bf-8c01-1f5e55ddf5de',
      details: '6387e7da-6776-47c2-b229-370a8f04c647',
      vendorSku: '24a5db90-194c-4820-a145-37de6e5613b2',
      vendorSkuDescription: '39919ebb-729c-40c0-af0d-6b128317621e',
      uom: 'ee27c642-cb4b-448e-ae2c-18b959da4e23',
      itemQty: 'b1ef4f5c-3956-4edf-83ba-c30ab82552db',
      unitPrice: 'dee0ff94-27db-47da-b30c-1e7475a30b41',
      freightTerms: 'b7d8c68d-264c-42f7-94d7-d50aa32319c7',
      lineTotal: '1579a449-e2e0-4315-a4d4-0c55487bcacd',
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: '55270148-27c0-4421-bc3a-77f84818470c',
      skuDescription: '6fbd972c-9973-48e2-b465-b439d88d7ab4',
      details: 'a3ea677f-9714-4c70-b6a4-833adb9ac4ed',
      vendorSku: '9c44cc4c-b8ad-42bc-be5a-7ca86d02b627',
      vendorSkuDescription: '97eee070-e6bd-4735-ba39-07c3cce0a8e9',
      uom: '20221400-42f2-4488-9fe0-a0a6cb98530a',
      itemQty: 'cd320993-d7a9-411d-84f9-64d9572bdc38',
      unitPrice: '23f6f529-1eb6-496e-92e0-0df54742f1f8',
      freightTerms: '95d64002-7dcf-4391-a3ce-9cb47f8ac161',
      lineTotal: 'e77a772b-96aa-4bc1-b5cf-d2a096e5d222',
    },
  ],
  dontRoundUnitPrice: '964057bc-95ea-45ed-bd41-5a21f08bd262',
  agreement: 'de1efa47-e803-43dd-93e9-f83bfc16f213',
  instructions: 'd1a75eb1-50c7-4f49-b150-661bf611ba24',
  notes: 'ed8d89ec-f6d4-4d55-a0e6-47e1032299a0',
  useLocaleQuantityFormatting: 'b6ec5811-5566-45af-8ef2-b2c308b55a8c',
  termsAndConditionsUrl: 'b8619976-3bd5-460f-820a-fde672a2dd77',
  termsAndConditionsLabel: '960ab3b4-673b-4dbe-b4c2-d0e236dd55ca',
  miscCharges: [
    {
      label: 'a7f5f7c9-0664-479a-b0d9-bbbb64fe5b91',
      value: '8ff974a6-b6a6-4688-911c-a506c2be1f3d',
      percentage: 'f351dce7-4077-49a4-be32-dab0cd3539f7',
    },
  ],
  currency: '701bbba8-b962-4576-bba6-b0564b24bd4b',
}
